import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService, plannedTotalDailyDose } from '../../services/dataService';
import { ClinicalRecord, HistoryRecord, InsulinScheduleView } from '../../types/domain';
import { buildReminders, summariseTodayGlucose } from '../../lib/reminders';
import {
  formatDateTime,
  getSiteLabelForHistory,
  getGlucoseZone,
  getMeasurementContextLabel
} from '../../lib/domain-data';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { isProfileComplete } from '../components/ProfileFields';
import { GamificationCard, useGamification } from '../components/Gamification';
import { cn } from '../../lib/utils';

// Four zones, matching .glucose-status in global.css (FR-07). The thresholds
// themselves live in getGlucoseZone so all three screens agree.
const glucoseZoneClasses: Record<string, string> = {
  'low': 'bg-sky-50/50 border-sky-100/50 text-sky-700',
  'normal': 'bg-emerald-50/50 border-emerald-100/50 text-emerald-700',
  'warning': 'bg-yellow-50/50 border-yellow-100/50 text-yellow-700',
  'high': 'bg-red-50/50 border-red-100/50 text-red-700'
};

/**
 * Rows Home reads from the log. Generous enough that a heavy day of recording
 * still leaves the previous day's last activity inside it, small enough that
 * the screen never pulls a whole history to show two numbers.
 */
const HOME_HISTORY_LIMIT = 60;

export default function Home() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [lastActivity, setLastActivity] = useState<HistoryRecord | null>(null);
  const [schedule, setSchedule] = useState<InsulinScheduleView[]>([]);
  // FR-19 scores against the schedule, so it waits for the real one.
  const [scheduleLoaded, setScheduleLoaded] = useState(false);
  const [todayInsulin, setTodayInsulin] = useState<{ total: number; injections: number } | null>(
    null
  );
  const [todayRecords, setTodayRecords] = useState<HistoryRecord[]>([]);
  const [clinicalRecords, setClinicalRecords] = useState<ClinicalRecord[]>([]);
  // FR-17's admin switch. Defaults to shown: offline the flag cannot be read,
  // and hiding a feature because the network is down reads as it vanishing.
  const [photosEnabled, setPhotosEnabled] = useState(true);

  useEffect(() => {
    if (!user) {
      return;
    }
    let cancelled = false;
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    const today = local.toISOString().slice(0, 10);

    // One load for the whole dashboard. getHistory is cached and shared, so
    // today's rows and the last activity come from the same fetch rather than
    // three effects racing for the same data.
    //
    // Bounded (NFR-02): this screen reads today's rows and history[0], so the
    // newest page of the log is all of it that is ever looked at. HistoryList
    // is the screen that wants everything.
    Promise.all([
      dataService.getHistory(user.id, HOME_HISTORY_LIMIT),
      dataService.getSchedule(user.id),
      dataService.getClinicalRecords(user.id)
    ]).then(([history, entries, clinical]) => {
      if (cancelled) {
        return;
      }
      const todays = history.filter(r => r.date === today);
      setLastActivity(history[0] ?? null);
      setTodayRecords(todays);
      setSchedule(entries);
      setScheduleLoaded(true);
      setClinicalRecords(clinical);
      setTodayInsulin({
        total: todays.reduce((sum, r) => sum + (Number(r.insulinDose) || 0), 0),
        injections: todays.filter(r => r.insulinDose).length
      });
    });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    dataService
      .getAppSetting('consultation_photos_enabled', true)
      .then(value => setPhotosEnabled(value !== false));
  }, []);

  useEffect(() => {
    if (user) {
      dataService.migrateLocalData(user.id);
      dataService.syncOfflineData(user.id);

      const handleOnline = () => {
        dataService.syncOfflineData(user.id);
      };

      window.addEventListener('online', handleOnline);
      return () => {
        window.removeEventListener('online', handleOnline);
      };
    }
    return undefined;
  }, [user]);

  const gamification = useGamification(user?.id, scheduleLoaded ? schedule : null);

  const todayGlucose = summariseTodayGlucose(todayRecords);
  const reminders = buildReminders({
    today: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10),
    schedule,
    todayInjections: todayRecords,
    todayGlucoseCount: todayGlucose.count,
    clinicalRecords,
    nextControlDate: profile?.next_control_date ?? null
  });

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div
        className="screen-card"
        role="group"
        aria-labelledby="mode-step-title"
      >
        <div className="mb-6 pb-4 border-b border-slate-100">
          <div className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            Halo, {profile?.full_name || 'Adik'}! <span className="animate-wave">👋</span>
          </div>

          {/* FR-02 fields are required, but a profile created before they
              existed has none of them. Asked for here rather than enforced by
              a redirect — see isProfileComplete(). */}
          {profile && !isProfileComplete(profile) ? (
            <button
              type="button"
              className="profile-nudge profile-nudge--action"
              onClick={() => navigate('/profile')}
            >
              <span>Profil belum lengkap. Yuk lengkapi datanya!</span>
              <span aria-hidden="true">→</span>
            </button>
          ) : null}

          <GamificationCard summary={gamification} />

          {lastActivity ? (
            <Card className="mb-6 bg-white/60 backdrop-blur-sm border-indigo-50 shadow-sm">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-bold text-indigo-500 uppercase tracking-wider flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                    Aktivitas Terakhir
                  </CardTitle>
                  <Badge variant="outline" className="font-normal text-xs text-slate-500 border-indigo-100 bg-white/50">
                    {formatDateTime(lastActivity.date, lastActivity.time)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                <div className="flex justify-between items-start border-b border-indigo-50/50 pb-2">
                  <span className="text-xs text-slate-500 font-medium shrink-0 mt-0.5">
                    {lastActivity.siteId === null ? 'Tindakan' : 'Lokasi'}
                  </span>
                  <span className="text-sm font-semibold text-slate-700 text-right leading-tight max-w-[70%]">
                    {getSiteLabelForHistory(lastActivity.ageGroup, lastActivity.siteId)}
                  </span>
                </div>

                {lastActivity.insulinBrand && (
                  <div className="flex justify-between items-center border-b border-indigo-50/50 pb-2">
                    <span className="text-xs text-slate-500 font-medium">Merk</span>
                    <span className="text-sm font-semibold text-slate-700">
                      {lastActivity.insulinBrand}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 pt-1">
                  {lastActivity.insulinDose !== undefined && lastActivity.insulinDose !== null && (
                    <div className="bg-blue-50/50 rounded-xl p-3 text-center border border-blue-100/50">
                      <div className="text-[10px] text-blue-400 font-bold uppercase mb-0.5">Dosis</div>
                      <div className="text-lg font-bold text-blue-700">
                        {lastActivity.insulinDose} <span className="text-xs font-medium opacity-70">unit</span>
                      </div>
                    </div>
                  )}

                  {lastActivity.glucoseValue !== undefined && lastActivity.glucoseValue !== null && (
                    <div className={cn(
                      "rounded-xl p-3 text-center border",
                      glucoseZoneClasses[getGlucoseZone(lastActivity.glucoseValue) ?? 'normal']
                    )}>
                      <div className="text-[10px] font-bold uppercase mb-0.5 opacity-70">Gula Darah</div>
                      <div className="text-lg font-bold">
                        {lastActivity.glucoseValue} <span className="text-xs font-medium opacity-70">mg/dL</span>
                      </div>
                      <div className="text-[10px] font-medium mt-0.5 opacity-70">
                        {getMeasurementContextLabel(lastActivity.measurementContext)}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="mb-6 bg-blue-50 border-blue-100">
              <CardContent className="p-4 flex items-center gap-3">
                <span className="text-xl">✨</span>
                <span className="font-medium text-blue-800 text-sm">Belum ada riwayat suntikan. Yuk mulai!</span>
              </CardContent>
            </Card>
          )}
        </div>

        {schedule.length > 0 ? (
          <div className="tdd-summary">
            <div className="tdd-summary-item">
              <span className="tdd-summary-label">TDD rencana</span>
              <span className="tdd-summary-value">
                {plannedTotalDailyDose(schedule)} <span className="tdd-unit">unit</span>
              </span>
              <span className="tdd-summary-note">{schedule.length} jadwal</span>
            </div>
            <div className="tdd-summary-item">
              <span className="tdd-summary-label">Sudah disuntik hari ini</span>
              <span className="tdd-summary-value">
                {todayInsulin?.total ?? 0} <span className="tdd-unit">unit</span>
              </span>
              <span className="tdd-summary-note">
                {todayInsulin?.injections ?? 0} dari {schedule.length} suntikan
              </span>
            </div>
          </div>
        ) : null}

        <div className="tdd-summary">
          <div className="tdd-summary-item">
            <span className="tdd-summary-label">Gula darah hari ini</span>
            <span className="tdd-summary-value">
              {todayGlucose.latest ?? '–'}{' '}
              <span className="tdd-unit">mg/dL</span>
            </span>
            <span className="tdd-summary-note">
              {todayGlucose.count === 0
                ? 'Belum ada catatan'
                : `${todayGlucose.count} kali · rata-rata ${todayGlucose.average}`}
            </span>
          </div>
          {clinicalRecords.find(r => r.hba1c !== null) ? (
            <div className="tdd-summary-item">
              <span className="tdd-summary-label">HbA1c terakhir</span>
              <span className="tdd-summary-value">
                {clinicalRecords.find(r => r.hba1c !== null)?.hba1c}{' '}
                <span className="tdd-unit">%</span>
              </span>
              <span className="tdd-summary-note">
                {clinicalRecords.find(r => r.hba1c !== null)?.recordedAt}
              </span>
            </div>
          ) : null}
        </div>

        {reminders.length > 0 ? (
          <div className="reminders">
            <div className="subtitle">Pengingat</div>
            <ul className="reminder-list">
              {reminders.map(reminder => (
                <li
                  key={reminder.kind}
                  className={`reminder reminder--${reminder.status}`}
                >
                  <span className="reminder-icon" aria-hidden="true">
                    {reminder.status === 'done' ? '✓' : reminder.status === 'soon' ? '•' : '!'}
                  </span>
                  <span className="reminder-text">
                    <span className="reminder-title">{reminder.title}</span>
                    <span className="reminder-detail">{reminder.detail}</span>
                  </span>
                  {reminder.status !== 'done' ? (
                    <button
                      type="button"
                      className="reminder-action"
                      onClick={() =>
                        navigate(
                          reminder.kind === 'glucose'
                            ? '/check-glucose'
                            : reminder.kind === 'insulin'
                              ? '/inject-insulin'
                              : '/clinical-data'
                        )
                      }
                    >
                      {/* A visit is not something to record — it is a date to check. */}
                      {reminder.kind === 'visit' ? 'Lihat' : 'Catat'}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div id="mode-step-title" className="section-title">
          Mau apa sekarang?
        </div>
        <div className="subtitle">Pilih permainan yang sesuai kegiatan Adik.</div>
        <div className="mode-grid cursor-pointer">
          <button
            type="button"
            className="mode-card hover:border-indigo-500 hover:shadow-md transition-all"
            onClick={() => navigate('/check-glucose')}
          >
            Cek gula darah
          </button>
          <button
            type="button"
            className="mode-card hover:border-indigo-500 hover:shadow-md transition-all"
            onClick={() => navigate('/inject-insulin')}
          >
            Suntik insulin
          </button>
          <button
            type="button"
            className="mode-card hover:border-indigo-500 hover:shadow-md transition-all"
            onClick={() => navigate('/insulin-schedule')}
          >
            Jadwal insulin
          </button>
          <button
            type="button"
            className="mode-card hover:border-indigo-500 hover:shadow-md transition-all"
            onClick={() => navigate('/clinical-data')}
          >
            Data klinis
          </button>
          <button
            type="button"
            className="mode-card hover:border-indigo-500 hover:shadow-md transition-all"
            onClick={() => navigate('/monthly-report')}
          >
            Laporan bulanan
          </button>
          <button
            type="button"
            className="mode-card hover:border-indigo-500 hover:shadow-md transition-all"
            onClick={() => navigate('/prestasi')}
          >
            Prestasi
          </button>
          {photosEnabled ? (
            <button
              type="button"
              className="mode-card hover:border-indigo-500 hover:shadow-md transition-all"
              onClick={() => navigate('/consultation-photos')}
            >
              Foto konsultasi
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
